import { isPaidAdVisit, type VisitUtm } from './isPaidAdVisit';
import { readCookie } from './readCookie';

const AD_PROFESSIONAL_COOKIE = '__ap_ad_professional';

/**
 * How long the ad's professional stays associated with the visitor.
 *
 * Matched to Meta's default click attribution window. A conversion the platform
 * still credits to the ad but that we no longer recognise as belonging to that
 * client is a conversion missing from the client's optimisation count, so a
 * shorter window costs conversions directly.
 */
const AD_PROFESSIONAL_TTL_SECONDS = 7 * 24 * 60 * 60;

/**
 * The professional whose ad brought the visitor to the site, and the campaign
 * that did it.
 */
export interface AdProfessional extends VisitUtm {
    professionalId: number;
}

/**
 * Reads the professional whose ad brought the visitor here, or null if they did
 * not arrive from one.
 *
 * A stored record is held to the same paid-ad rule as a write, so a record whose
 * UTM tagging does not mark a paid ad reads as null.
 *
 * Held in a cookie rather than session storage so it survives the visitor
 * leaving and coming back within the attribution window, and so both the Remix
 * app and the Business Manager can read it without sharing client-side state.
 */
export function readAdProfessional(): AdProfessional | null {
    if (typeof document === 'undefined') {
        return null;
    }

    const raw = readCookie(AD_PROFESSIONAL_COOKIE);
    if (!raw) {
        return null;
    }

    try {
        return parseAdProfessional(JSON.parse(raw));
    } catch {
        // A malformed cookie means no attribution, not a broken conversion.
        return null;
    }
}

/**
 * Records the professional whose ad brought the visitor here, when the visit's
 * UTM tagging marks it as a paid ad.
 *
 * A visit from any other tagged link records nothing and leaves an earlier ad's
 * professional in place: the ad platform keeps crediting that ad click for the
 * rest of its attribution window.
 */
export function writeAdProfessional(value: AdProfessional): void {
    if (typeof document === 'undefined' || !isPaidAdVisit(value)) {
        return;
    }

    const encoded = encodeURIComponent(JSON.stringify(value));
    const attributes = [
        `${AD_PROFESSIONAL_COOKIE}=${encoded}`,
        'path=/',
        `max-age=${AD_PROFESSIONAL_TTL_SECONDS}`,
        'SameSite=Lax',
    ];

    if (window.location.protocol === 'https:') {
        attributes.push('Secure');
    }

    document.cookie = attributes.join('; ');
}

/**
 * Narrows an unknown parsed cookie payload to a usable attribution record, or
 * null when it names no professional or is not from a paid ad.
 */
function parseAdProfessional(value: unknown): AdProfessional | null {
    if (typeof value !== 'object' || value === null) {
        return null;
    }

    const record = value as Record<string, unknown>;
    const professionalId = Number(record['professionalId']);

    if (!Number.isInteger(professionalId) || professionalId <= 0) {
        return null;
    }

    const adProfessional = {
        professionalId,
        utmCampaign: asString(record['utmCampaign']),
        utmSource: asString(record['utmSource']),
        utmMedium: asString(record['utmMedium']),
    };

    return isPaidAdVisit(adProfessional) ? adProfessional : null;
}

function asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
}
